"use client";

import React, { useState, useEffect } from "react";
import {
  ShieldCheck,
  Search,
  Filter,
  Plus,
  RefreshCw,
  TrendingUp,
  FileCheck2,
  Stethoscope,
  DollarSign,
  Activity,
} from "lucide-react";
import InsuranceTopBar from "./InsuranceTopBar";
import InsuranceKanban from "./InsuranceKanban";
import QuoteProposalModal from "./QuoteProposalModal";
import { InsurancePolicyLead } from "@/lib/demos/insuranceDb";
import { INSURANCE_PRODUCTS } from "@/lib/demos/insuranceData";

export default function InsuranceCrmDashboard() {
  const [policies, setPolicies] = useState<InsurancePolicyLead[]>([]);
  const [stats, setStats] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProductFilter, setSelectedProductFilter] = useState("ALL");
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");
  const [isNewProposalModalOpen, setIsNewProposalModalOpen] = useState(false);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/demos/insurance/policies");
      const data = await res.json();
      if (data.success) {
        setPolicies(data.policies || []);
        setStats(data.stats || null);
      }
    } catch (err) {
      console.error("Failed to fetch policies", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleUpdatePolicy = async (
    id: number,
    updates: Partial<InsurancePolicyLead> & { sendEmail: boolean }
  ) => {
    try {
      const res = await fetch(`/api/demos/insurance/policies/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.success && data.policy) {
        setPolicies((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...data.policy } : p))
        );
        // Refresh stats
        fetchPolicies();
      }
    } catch (err) {
      console.error("Failed to update policy", err);
    }
  };

  // Filter policies
  const filteredPolicies = policies.filter((p) => {
    const matchesSearch =
      p.prospect_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.proposal_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.policy_number && p.policy_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
      p.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesProduct =
      selectedProductFilter === "ALL" || p.product_id === selectedProductFilter;

    return matchesSearch && matchesProduct;
  });

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Top Header */}
      <InsuranceTopBar
        activeTab="crm"
        currency={currency}
        onCurrencyToggle={setCurrency}
        onResetDemo={fetchPolicies}
      />

      <div className="max-w-[1600px] w-full mx-auto px-4 lg:px-8 py-6 space-y-6 flex-1">
        
        {/* Top Control Title Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                Underwriting, Risk &amp; Policy CRM
              </h1>
              <span className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-950/90 text-emerald-400 border border-emerald-800">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live Sync
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Drag proposals across stages to trigger automated medical passes &amp; IRDAI policy schedules via Hostinger SMTP.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setIsNewProposalModalOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-lg shadow-blue-950 transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>New Proposal Intake</span>
            </button>
            <button
              onClick={fetchPolicies}
              title="Refresh Pipeline"
              className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-blue-400" : ""}`} />
            </button>
          </div>
        </div>

        {/* 4 KPI Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Total Proposals
              </div>
              <div className="text-xl sm:text-2xl font-black text-white mt-1">
                {stats?.totalPolicies ?? policies.length}
              </div>
              <div className="text-[10px] text-blue-400 mt-0.5">Active Ingestion Cases</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
              <FileCheck2 className="w-5 h-5" />
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                In Underwriting
              </div>
              <div className="text-xl sm:text-2xl font-black text-indigo-400 mt-1">
                {stats?.underReview ?? 0}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">Lab &amp; Tele-MER Review</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-indigo-600/10 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
              <Stethoscope className="w-5 h-5" />
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Active Policies
              </div>
              <div className="text-xl sm:text-2xl font-black text-emerald-400 mt-1">
                {stats?.activePolicies ?? 0}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">IRDAI Policies In Force</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-600/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/70 border border-slate-800 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Gross Written Premium
              </div>
              <div className="text-xl sm:text-2xl font-black text-white mt-1">
                {sym}{stats?.grossPremium ? stats.grossPremium.toLocaleString() : "0"}
              </div>
              <div className="text-[10px] text-emerald-400 mt-0.5">Annualized Persistency</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-600/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 bg-slate-900/80 border border-slate-800 rounded-2xl">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by prospect, policy no, proposal..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
            <Filter className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <button
              onClick={() => setSelectedProductFilter("ALL")}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                selectedProductFilter === "ALL"
                  ? "bg-blue-600 text-white"
                  : "bg-slate-950 text-slate-400 hover:text-white"
              }`}
            >
              All Plans
            </button>
            {INSURANCE_PRODUCTS.map((prod) => (
              <button
                key={prod.id}
                onClick={() => setSelectedProductFilter(prod.id)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  selectedProductFilter === prod.id
                    ? "bg-blue-600 text-white"
                    : "bg-slate-950 text-slate-400 hover:text-white"
                }`}
              >
                {prod.name.split(" ")[0]} {prod.name.split(" ")[1]}
              </button>
            ))}
          </div>
        </div>

        {/* Kanban Board */}
        <InsuranceKanban
          policies={filteredPolicies}
          onUpdatePolicy={handleUpdatePolicy}
        />

      </div>

      {/* New Proposal Intake Modal */}
      <QuoteProposalModal
        isOpen={isNewProposalModalOpen}
        onClose={() => {
          setIsNewProposalModalOpen(false);
          fetchPolicies();
        }}
        currency={currency}
      />
    </div>
  );
}
